import { F0Form, f0FormField, useF0Form, useF0FormDefinition } from '@factorialco/f0-react'
import { useEffect } from 'react'
import { z } from 'zod'

import i18n from 'modules/core/lib/i18n'
import useSubmissionPeriods from 'modules/planning/hooks/useSubmissionPeriods'

interface Props {
  periodId: string
  companyId: string
  submissionPeriodId?: string
  onClose: () => void
  onStatusChange: (status: {
    submit: () => void
    isSubmitting: boolean
    hasErrors: boolean
  }) => void
}

const combineDateAndTime = (date: Date, time: Date): string => {
  const result = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes(),
    0
  )
  return result.toISOString()
}

const Launch = ({ periodId, companyId, submissionPeriodId, onClose, onStatusChange }: Props) => {
  const { launch } = useSubmissionPeriods({ periodId, companyId, submissionPeriodId })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  useEffect(() => {
    onStatusChange({ submit, isSubmitting, hasErrors })
  }, [submit, isSubmitting, hasErrors, onStatusChange])

  const oneMonthFromNow = new Date()
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1)

  const schema = z.object({
    endDate: f0FormField(z.date(), {
      label: i18n.t('planning.submission_period.form.end_date'),
      fieldType: 'date',
      row: 'endRow',
    }),
    endTime: f0FormField(z.date(), {
      label: i18n.t('planning.submission_period.form.end_time'),
      fieldType: 'time',
      row: 'endRow',
    }),
  })

  const formDefinition = useF0FormDefinition({
    name: 'launch-submission-period',
    schema,
    defaultValues: {
      endDate: oneMonthFromNow,
      endTime: oneMonthFromNow,
    },
    onSubmit: async ({ data }) => {
      const endDate = combineDateAndTime(data.endDate, data.endTime)

      const { success, errors } = await launch(endDate)

      if (!success) {
        return { success: false, errors }
      }

      onClose()
      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return <F0Form formRef={formRef} formDefinition={formDefinition} />
}

export default Launch
